import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { Spinner } from '../../components/ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatDate } from '../../utils/formatters.js';

/**
 * Der Monatsrapport zum Unterschreiben.
 *
 * Die erste Druckseite des Personalbereichs. Bisher musste für den
 * Treuhänder oder eine Kontrolle jemand abschreiben.
 *
 * Gedruckt wird JEDER Tag des Zeitraums, auch der leere. Ein Rapport, der
 * nur die erfassten Tage zeigt, verschweigt gerade das, wofür man ihn
 * unterschreibt - dass an den übrigen Tagen nichts offen ist.
 *
 * Und JEDE Zeile eines Tages, nicht nur die erste. Wer vormittags und
 * nachmittags stempelt, hat zwei Zeilen; nur die erste zu drucken hiesse,
 * neben acht Stunden Ist vier Stunden Uhrzeit zu setzen und den Vormittag
 * verschwinden zu lassen - auf genau dem Blatt, das unterschrieben wird.
 */

/** ISO-Wochentag, 1 = Montag. In UTC, sonst rutscht der Tag in Mitteleuropa. */
function wochentag(iso) {
  const wd = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return wd === 0 ? 7 : wd;
}

const WOCHENTAG_KEYS = [
  'weekday.short.mon',
  'weekday.short.tue',
  'weekday.short.wed',
  'weekday.short.thu',
  'weekday.short.fri',
  'weekday.short.sat',
  'weekday.short.sun',
];

function signiert(wert) {
  const n = Number(wert ?? 0);
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}`;
}

/**
 * Die Zeilen eines Tages, so wie sie gedruckt werden.
 *
 * Ein Tag ohne Erfassung ergibt EINE leere Zeile - er gehört ins Blatt.
 * Ein Tag mit drei Stempelungen ergibt drei.
 */
function zeilenFuer(tag) {
  const eintraege = tag.entries || [];
  if (eintraege.length === 0) {
    return [{ key: tag.date, eintrag: null }];
  }
  return eintraege.map((e, i) => ({ key: `${tag.date}-${e.id ?? i}`, eintrag: e }));
}

export function MonthlyReportPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const employeeId = params.get('employeeId') || '';
  const from = params.get('from') || '';
  const to = params.get('to') || '';

  const mitarbeitende = useQuery({
    queryKey: ['hr', 'employees'],
    queryFn: () => hrApi.listEmployees({ limit: 200 }).then((r) => r.items || []),
  });

  /* Die Wochenansicht liefert JEDEN Tag mit Soll, Ist und den Zeilen - genau
     das, was ein Rapport braucht. Sie ist nicht auf sieben Tage beschränkt;
     der Name führt in die Irre, die Sache stimmt. */
  const raster = useQuery({
    queryKey: ['hr', 'time-week', employeeId, from, to],
    queryFn: () => hrApi.timeWeek({ employeeId, from, to }).then((r) => r.items),
    enabled: Boolean(employeeId && from && to),
  });

  /* Beide Pflichtangaben zusammen prüfen. Vorher fing der Wachposten nur
     die fehlende Person ab; ohne Zeitraum lief die Abfrage gar nicht los,
     und weil eine abgeschaltete Abfrage weder lädt noch scheitert, stand
     unter dem Kopf eine Fehlermeldung ohne Fehler. */
  if (!employeeId || !from || !to) {
    return (
      <section className="page">
        <div className="alert warning">
          {!employeeId ? t('hrtime.report.pickEmployee') : t('hrtime.report.pickRange')}
        </div>
        <Link to="/hr/time" className="btn">
          ← {t('common.back')}
        </Link>
      </section>
    );
  }

  if (raster.isLoading) {
    return (
      <section className="page">
        <Spinner />
      </section>
    );
  }

  if (raster.error || !raster.data) {
    return (
      <section className="page">
        <div className="alert danger">{raster.error?.message || t('common.loadError')}</div>
        <Link to="/hr/time" className="btn">
          ← {t('common.back')}
        </Link>
      </section>
    );
  }

  const person = (mitarbeitende.data || []).find((e) => e.id === employeeId);
  const tage = raster.data.days || [];

  return (
    <div className="invoice-print-wrap">
      <div className="invoice-toolbar no-print">
        <Link to="/hr/time" className="btn">
          ← {t('common.back')}
        </Link>
        <div className="invoice-toolbar-actions">
          <button type="button" className="btn primary" onClick={() => window.print()}>
            {t('invoice.print.printOrSave')}
          </button>
        </div>
      </div>

      <article className="invoice-doc">
        <header className="invoice-doc-head">
          <div>
            <h1>{t('hrtime.report.title')}</h1>
            <p className="invoice-doc-muted">
              {formatDate(from)} - {formatDate(to)}
            </p>
          </div>
          <div className="invoice-doc-meta">
            <div>
              <span className="invoice-doc-muted">{t('hr.leave.col.employee')}</span>
              {/* Ohne geladene Personalakte KEIN Kennungsfragment: 'a3f9c1d2'
                  auf einem Unterschriftsdokument ist schlechter als ein
                  ehrlicher Hinweis. */}
              <strong>
                {person
                  ? `${person.firstName} ${person.lastName}`
                  : mitarbeitende.isLoading
                    ? '…'
                    : t('hrtime.report.unknownEmployee')}
              </strong>
            </div>
            {person?.employeeNumber && (
              <div>
                <span className="invoice-doc-muted">{t('hrtime.report.employeeNumber')}</span>
                <strong className="mono">{person.employeeNumber}</strong>
              </div>
            )}
          </div>
        </header>

        <table className="invoice-doc-table report-table">
          <thead>
            <tr>
              <th>{t('hrtime.col.date')}</th>
              <th>{t('hrtime.col.type')}</th>
              <th>{t('time.add.start')}</th>
              <th>{t('time.add.end')}</th>
              <th className="num">{t('time.add.break')}</th>
              <th className="num">{t('hrtime.col.hours')}</th>
              <th className="num">{t('month.target')}</th>
              <th className="num">{t('month.balance')}</th>
            </tr>
          </thead>
          <tbody>
            {tage.map((tag) => {
              const zeilen = zeilenFuer(tag);
              const abweichung = Number(tag.actualHours || 0) - Number(tag.targetHours || 0);
              return zeilen.map((zeile, i) => (
                <tr key={zeile.key} data-off={tag.targetHours === 0 || undefined}>
                  {/* Datum, Soll und Abweichung gehören dem TAG, nicht der
                      Zeile - sie stehen deshalb nur an der ersten. */}
                  <td>
                    {i === 0 && (
                      <>
                        <span className="mono">
                          {tag.date.slice(8)}.{tag.date.slice(5, 7)}.
                        </span>{' '}
                        <span className="invoice-doc-muted">
                          {t(WOCHENTAG_KEYS[wochentag(tag.date) - 1])}
                        </span>
                        {tag.isHoliday && (
                          <span className="invoice-doc-muted"> · {t('time.week.holiday')}</span>
                        )}
                      </>
                    )}
                  </td>
                  {/* Die ART, ausgeschrieben. Eine genehmigte Abwesenheit
                      erzeugt eine Zeile in der Zeiterfassung; ohne diese
                      Spalte stünden Ferien auf dem Unterschriftsblatt als
                      geleistete Arbeitszeit - acht Stunden Ist, kein Kommen,
                      kein Gehen, nicht zu unterscheiden von einem Tag im
                      Büro, an dem jemand zu stempeln vergass. */}
                  <td>{zeile.eintrag ? t(`hrtime.type.${zeile.eintrag.type}`) : ''}</td>
                  <td className="mono">{zeile.eintrag?.startTime || ''}</td>
                  <td className="mono">{zeile.eintrag?.endTime || ''}</td>
                  <td className="num mono">{zeile.eintrag?.breakMinutes || ''}</td>
                  <td className="num mono">
                    {zeile.eintrag ? Number(zeile.eintrag.hours).toFixed(2) : '0.00'}
                  </td>
                  <td className="num mono">{i === 0 ? Number(tag.targetHours).toFixed(2) : ''}</td>
                  <td className="num mono">
                    {i === 0 && abweichung !== 0 ? signiert(abweichung) : ''}
                  </td>
                </tr>
              ));
            })}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={5}>{t('hrtime.report.sum')}</th>
              <th className="num mono">{Number(raster.data.actualHours).toFixed(2)}</th>
              <th className="num mono">{Number(raster.data.targetHours).toFixed(2)}</th>
              <th className="num mono">{signiert(raster.data.balanceHours)}</th>
            </tr>
          </tfoot>
        </table>

        {/* Der Grund, warum es diese Seite gibt: darunter wird unterschrieben. */}
        <div className="report-signatures">
          <div>
            <span className="report-sig-line" />
            <span className="invoice-doc-muted">{t('hrtime.report.signEmployee')}</span>
          </div>
          <div>
            <span className="report-sig-line" />
            <span className="invoice-doc-muted">{t('hrtime.report.signManager')}</span>
          </div>
        </div>
      </article>
    </div>
  );
}
